// ─────────────────────────────────────────────────────────────────────────────
// Archivo: reportesGastosService.js
// Ubicación sugerida: src/services/reportesGastosService.js
//
// Reemplaza toda la lógica de useMemo del componente ReportesGastos.
// Cada función llama a un endpoint distinto y devuelve los datos
// ya calculados por el backend.
//
// Uso en el componente:
//
//   import * as reportesGastosService from '../../services/reportesGastosService';
//
//   const resumen  = await reportesGastosService.getResumen(filtros);
//   const porTipo  = await reportesGastosService.getPorTipo(filtros);
//   const porMes   = await reportesGastosService.getPorMes(filtros);
//   const porMedico = await reportesGastosService.getPorMedico(filtros);
//   const detalle  = await reportesGastosService.getDetalle(filtros);
// ─────────────────────────────────────────────────────────────────────────────

import apiClient from '../apiClient';

const BASE_URL = '/reportes/gastos';

// ── Helper: construir query string a partir del objeto de filtros ─────────────
/**
 * @param {Object} filtros
 * @param {string|null} filtros.fechaDesde  — "yyyy-MM-dd" o null
 * @param {string|null} filtros.fechaHasta  — "yyyy-MM-dd" o null
 * @param {number|null} filtros.idMedico
 * @param {number|null} filtros.idTipoTramite
 * @returns {Object} objeto de parámetros para axios
 */
function buildParams(filtros = {}) {
    const params = {};
    if (filtros.fechaDesde) params.fechaDesde = filtros.fechaDesde;
    if (filtros.fechaHasta) params.fechaHasta = filtros.fechaHasta;
    if (filtros.idMedico) params.idMedico = filtros.idMedico;
    if (filtros.idTipoTramite) params.idTipoTramite = filtros.idTipoTramite;
    return params;
}

// ── 1. Resumen ejecutivo ──────────────────────────────────────────────────────
/**
 * GET /reportes/gastos/resumen
 *
 * Devuelve los KPIs principales: totales, tendencia, tipo con mayor gasto, etc.
 *
 * @param {Object} filtros
 * @returns {Promise<{
 *   totalGastado: number,
 *   cantidadGastos: number,
 *   gastoPromedio: number,
 *   totalMedicos: number,
 *   cantidadMedicos: number,
 *   totalNoMedicos: number,
 *   cantidadNoMedicos: number,
 *   pctMedicos: number,
 *   pctNoMedicos: number,
 *   tendenciaUltimoMes: number|null,
 *   tendenciaSube: boolean|null,
 *   tipoMayorGastoNombre: string|null,
 *   tipoMayorGastoMonto: number|null,
 *   mesMayorGasto: string|null,       — "YYYY-MM"
 *   mesMayorGastoMonto: number|null
 * }>}
 */
export async function getResumen(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/resumen`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 2. Por tipo de trámite ────────────────────────────────────────────────────
/**
 * GET /reportes/gastos/por-tipo
 *
 * Devuelve un array ordenado por monto descendente.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idTipoTramite: number,
 *   nombreTipo: string,
 *   totalMonto: number,
 *   cantidad: number,
 *   porcentaje: number
 * }>>}
 */
export async function getPorTipo(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-tipo`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 3. Evolución mensual ──────────────────────────────────────────────────────
/**
 * GET /reportes/gastos/por-mes
 *
 * Devuelve un array cronológico. El componente lo transforma en las
 * series del gráfico de líneas (total / médicos / no médicos).
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   anio: number,
 *   mes: number,
 *   totalMedicos: number,
 *   totalNoMedicos: number,
 *   totalGeneral: number,
 *   cantidadMedicos: number,
 *   cantidadNoMedicos: number
 * }>>}
 */
export async function getPorMes(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-mes`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 4. Por médico ─────────────────────────────────────────────────────────────
/**
 * GET /reportes/gastos/por-medico
 *
 * Solo médicos con registro en la tabla medico (conocidos).
 * Ordenado por monto descendente.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idMedico: number,
 *   nombreMedico: string,
 *   especialidad: string,
 *   totalMonto: number,
 *   cantidad: number,
 *   porcentaje: number
 * }>>}
 */
export async function getPorMedico(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-medico`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 5. Detalle completo ───────────────────────────────────────────────────────
/**
 * GET /reportes/gastos/detalle
 *
 * Listado completo para la tabla y el PDF. Ordenado por fecha desc.
 * nombreMedico puede ser null si el gasto no tiene médico asociado.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idGasto: number,
 *   fecha: string,           — "dd/MM/yyyy"
 *   nombreTipo: string,
 *   idMedico: number|null,
 *   nombreMedico: string|null,
 *   monto: number,
 *   comentario: string|null
 * }>>}
 */
export async function getDetalle(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/detalle`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── Helper: llamar todos los endpoints en paralelo ────────────────────────────
/**
 * Llama a todos los endpoints en paralelo con Promise.all.
 * Útil para la carga inicial del componente.
 *
 * @param {Object} filtros
 * @returns {Promise<{
 *   resumen: Object,
 *   porTipo: Array,
 *   porMes: Array,
 *   porMedico: Array,
 *   detalle: Array
 * }>}
 */
export async function getAllReportes(filtros = {}) {
    const [resumen, porTipo, porMes, porMedico, detalle] = await Promise.all([
        getResumen(filtros),
        getPorTipo(filtros),
        getPorMes(filtros),
        getPorMedico(filtros),
        getDetalle(filtros),
    ]);
    return { resumen, porTipo, porMes, porMedico, detalle };
}